import React from 'react';
import { Link, type Location } from 'react-router-dom';

interface AuthGateFallbackProps {
  message?: string;
  error?: string | null;
  /** Preserved for post-login redirect when user clicks "Sign in again". */
  returnLocation?: Location;
}

/**
 * Loading / error UI for auth gates (ProtectedRoute, app bootstrap).
 */
export function AuthGateFallback({
  message = 'Loading...',
  error = null,
  returnLocation,
}: AuthGateFallbackProps) {
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
            <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-lg font-semibold text-gray-900">Unable to verify your session</h1>
          <p className="mt-2 text-sm text-gray-600">{error}</p>
          <Link
            to="/login"
            state={{
              from: returnLocation,
              message: 'Please sign in to access this page.',
            }}
            className="mt-6 inline-flex items-center justify-center rounded-lg bg-purple-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-purple-700"
          >
            Sign in again
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-purple-600" />
        <p className="mt-4 text-gray-600">{message}</p>
      </div>
    </div>
  );
}
